import type { ReceiptDetailDto, TaxInvoiceCustomerDto } from '@pos/types';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Field } from '../../components/Field';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { getSettings, listBranches } from '../../data/fetchers';
import { dashboardApi } from '../../lib/apiClient';
import { fmtAmount, fmtDate } from '../../lib/format';
import { useSession } from '../../stores/session';
import type { SystemSettings } from '../../types/domain';

type BranchType = 'headquarters' | 'branch';

interface TaxInvoiceFormState {
  sellerName: string;
  sellerTaxId: string;
  sellerAddress: string;
  sellerBranchType: BranchType;
  sellerBranchNo: string;
  buyerName: string;
  buyerTaxId: string;
  buyerPhone: string;
  buyerAddress: string;
  buyerBranchType: BranchType;
  buyerBranchNo: string;
  note: string;
}

type TaxInvoiceErrors = Partial<Record<keyof TaxInvoiceFormState, string>>;

const CONTROL_CLASS =
  'h-10 rounded-btn border border-line bg-surface px-3 text-body focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15';
const TEXTAREA_CLASS =
  'min-h-16 resize-y rounded-btn border border-line bg-surface px-3 py-2 text-body focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15';

const digitsOnly = (value: string) => value.replace(/\D/g, '');

function createInitialForm(
  settings: SystemSettings,
  hasMultipleBranches: boolean,
  receipt: ReceiptDetailDto,
): TaxInvoiceFormState {
  const customer = receipt.taxInvoiceCustomer;
  return {
    sellerName: settings.shopName,
    sellerTaxId: settings.taxId,
    sellerAddress: '',
    sellerBranchType: hasMultipleBranches ? 'branch' : 'headquarters',
    sellerBranchNo: '',
    buyerName: customer?.name ?? '',
    buyerTaxId: customer?.taxId ?? '',
    buyerPhone: customer?.phone ?? '',
    buyerAddress: customer?.address ?? '',
    buyerBranchType: customer?.branchType ?? 'headquarters',
    buyerBranchNo: customer?.branchNo ?? '',
    note: '',
  };
}

function validate(form: TaxInvoiceFormState): TaxInvoiceErrors {
  const errors: TaxInvoiceErrors = {};
  const required: Array<keyof TaxInvoiceFormState> = [
    'sellerName',
    'sellerAddress',
    'buyerName',
    'buyerAddress',
  ];
  for (const field of required) {
    if (!form[field].trim()) errors[field] = 'กรุณากรอกข้อมูล';
  }

  if (digitsOnly(form.sellerTaxId).length !== 13) {
    errors.sellerTaxId = 'เลขผู้เสียภาษีต้องมี 13 หลัก';
  }
  if (digitsOnly(form.buyerTaxId).length !== 13) {
    errors.buyerTaxId = 'เลขผู้เสียภาษีต้องมี 13 หลัก';
  }
  const buyerPhoneDigits = digitsOnly(form.buyerPhone);
  if (
    form.buyerPhone.trim() &&
    (buyerPhoneDigits.length < 9 || buyerPhoneDigits.length > 15)
  ) {
    errors.buyerPhone = 'เบอร์โทรต้องมี 9–15 หลัก';
  }
  if (
    form.sellerBranchType === 'branch' &&
    digitsOnly(form.sellerBranchNo).length !== 5
  ) {
    errors.sellerBranchNo = 'เลขสาขาต้องมี 5 หลัก';
  }
  if (
    form.buyerBranchType === 'branch' &&
    digitsOnly(form.buyerBranchNo).length !== 5
  ) {
    errors.buyerBranchNo = 'เลขสาขาต้องมี 5 หลัก';
  }
  return errors;
}

function branchLabel(type: BranchType, branchNo: string): string {
  return type === 'headquarters'
    ? 'สำนักงานใหญ่'
    : `สาขา ${digitsOnly(branchNo).padStart(5, '0')}`;
}

function TaxIdInput({
  value,
  onChange,
  ...props
}: {
  value: string;
  onChange: (value: string) => void;
  'aria-label': string;
  disabled?: boolean;
}) {
  return (
    <Input
      {...props}
      inputMode="numeric"
      maxLength={17}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder="0-0000-00000-00-0"
    />
  );
}

function BranchFields({
  prefix,
  type,
  branchNo,
  error,
  disabled = false,
  onTypeChange,
  onBranchNoChange,
}: {
  prefix: 'ผู้ขาย' | 'ผู้ซื้อ';
  type: BranchType;
  branchNo: string;
  error?: string;
  disabled?: boolean;
  onTypeChange: (value: BranchType) => void;
  onBranchNoChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field
        className={type === 'headquarters' ? 'sm:col-span-2' : ''}
        label={`ประเภทสาขา${prefix}`}
        required
      >
        <select
          aria-label={`ประเภทสาขา${prefix}`}
          className={CONTROL_CLASS}
          value={type}
          disabled={disabled}
          onChange={(event) => onTypeChange(event.target.value as BranchType)}
        >
          <option value="headquarters">สำนักงานใหญ่</option>
          <option value="branch">สาขา</option>
        </select>
      </Field>
      {type === 'branch' && (
        <Field label={`เลขสาขา${prefix}`} required error={error}>
          <Input
            aria-label={`เลขสาขา${prefix}`}
            inputMode="numeric"
            maxLength={5}
            value={branchNo}
            disabled={disabled}
            onChange={(event) =>
              onBranchNoChange(digitsOnly(event.target.value))
            }
            placeholder="00001"
          />
        </Field>
      )}
    </div>
  );
}

function InvoiceDocument({
  receipt,
  branchName,
  form,
}: {
  receipt: ReceiptDetailDto;
  branchName: string;
  form: TaxInvoiceFormState;
}) {
  const lineSubtotal = receipt.lines.reduce(
    (total, line) => total + line.amount,
    0,
  );
  const promotionDiscount = receipt.appliedPromotions.reduce(
    (total, promotion) => total + promotion.discountAmount,
    0,
  );
  const totalDiscount = promotionDiscount + (receipt.billDiscount ?? 0);
  const netBeforeVat = receipt.totalAmount - receipt.vatAmount;

  return (
    <article
      aria-label="ตัวอย่างใบกำกับภาษี"
      className="tax-invoice-print-root min-h-[272mm] w-[210mm] bg-white p-[12mm] text-[12px] leading-relaxed text-slate-950 shadow-lg ring-1 ring-slate-200"
    >
      <header className="border-b-2 border-slate-900 pb-4 text-center">
        <div className="text-[11px] font-semibold tracking-[0.12em]">
          ต้นฉบับ / ORIGINAL
        </div>
        <h2 className="mt-1 text-[22px] font-bold">
          ใบกำกับภาษี / ใบเสร็จรับเงิน
        </h2>
        <div className="text-[11px] tracking-wide">TAX INVOICE / RECEIPT</div>
      </header>

      <div className="mt-4 grid grid-cols-[1fr_auto] gap-6">
        <section>
          <h3 className="text-[17px] font-bold">{form.sellerName || '-'}</h3>
          <p className="whitespace-pre-line">{form.sellerAddress || '-'}</p>
          <p>
            เลขประจำตัวผู้เสียภาษี {form.sellerTaxId || '-'}
            <br />
            {branchLabel(form.sellerBranchType, form.sellerBranchNo)}
          </p>
          <p>สาขาที่ขาย: {branchName}</p>
        </section>
        <dl className="grid grid-cols-[auto_auto] content-start gap-x-4 gap-y-1 text-right">
          <dt className="text-slate-500">เลขที่</dt>
          <dd className="font-bold tabular-nums">{receipt.receiptNo}</dd>
          <dt className="text-slate-500">วันที่</dt>
          <dd className="tabular-nums">{fmtDate(new Date(receipt.soldAt))}</dd>
          <dt className="text-slate-500">อ้างอิง</dt>
          <dd className="tabular-nums">{receipt.receiptNo}</dd>
        </dl>
      </div>

      <section className="mt-5 rounded border border-slate-400 p-3">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
          ลูกค้า / Customer
        </div>
        <div className="font-bold">{form.buyerName || '-'}</div>
        <div className="whitespace-pre-line">{form.buyerAddress || '-'}</div>
        <div>
          เลขประจำตัวผู้เสียภาษี {form.buyerTaxId || '-'}
          {form.buyerPhone && (
            <>
              <br />
              โทร {form.buyerPhone}
            </>
          )}
          <br />
          {branchLabel(form.buyerBranchType, form.buyerBranchNo)}
        </div>
      </section>

      <table className="mt-5 w-full border-collapse">
        <thead>
          <tr className="border-y-2 border-slate-900 text-left">
            <th className="w-10 py-2 text-center">ลำดับ</th>
            <th className="py-2">รายการ</th>
            <th className="w-16 py-2 text-right">จำนวน</th>
            <th className="w-24 py-2 text-right">ราคา/หน่วย</th>
            <th className="w-24 py-2 text-right">จำนวนเงิน</th>
          </tr>
        </thead>
        <tbody>
          {receipt.lines.map((line, index) => (
            <tr key={line.id} className="border-b border-slate-200">
              <td className="py-2 text-center tabular-nums">{index + 1}</td>
              <td className="py-2">{line.productName ?? line.productId}</td>
              <td className="py-2 text-right tabular-nums">{line.quantity}</td>
              <td className="py-2 text-right tabular-nums">
                {fmtAmount(line.unitPrice)}
              </td>
              <td className="py-2 text-right tabular-nums">
                {fmtAmount(line.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 grid grid-cols-[1fr_250px] gap-8">
        <section className="min-h-24">
          {form.note.trim() && (
            <>
              <div className="text-slate-500">หมายเหตุ</div>
              <p className="whitespace-pre-line">{form.note}</p>
            </>
          )}
        </section>
        <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5">
          <dt>มูลค่าสินค้ารวม VAT</dt>
          <dd className="text-right tabular-nums">{fmtAmount(lineSubtotal)}</dd>
          {totalDiscount > 0 && (
            <>
              <dt>ส่วนลดรวม</dt>
              <dd className="text-right tabular-nums">
                -{fmtAmount(totalDiscount)}
              </dd>
            </>
          )}
          {(receipt.roundingDelta ?? 0) !== 0 && (
            <>
              <dt>ปรับเศษ</dt>
              <dd className="text-right tabular-nums">
                {(receipt.roundingDelta ?? 0) > 0 ? '+' : ''}
                {fmtAmount(receipt.roundingDelta ?? 0)}
              </dd>
            </>
          )}
          <dt>มูลค่าก่อน VAT</dt>
          <dd className="text-right tabular-nums">{fmtAmount(netBeforeVat)}</dd>
          <dt>ภาษีมูลค่าเพิ่ม (VAT)</dt>
          <dd className="text-right tabular-nums">
            {fmtAmount(receipt.vatAmount)}
          </dd>
          <dt className="border-t-2 border-slate-900 pt-2 text-[14px] font-bold">
            ยอดสุทธิ
          </dt>
          <dd className="border-t-2 border-slate-900 pt-2 text-right text-[14px] font-bold tabular-nums">
            {fmtAmount(receipt.totalAmount)}
          </dd>
        </dl>
      </div>

      <footer className="mt-16 grid grid-cols-2 gap-16 text-center">
        <div>
          <div className="border-b border-slate-600 pb-8" />
          <div className="pt-2">ผู้รับสินค้า / ผู้ชำระเงิน</div>
          <div>วันที่ ______ / ______ / ______</div>
        </div>
        <div>
          <div className="border-b border-slate-600 pb-8" />
          <div className="pt-2">ผู้รับเงิน / ผู้มีอำนาจลงนาม</div>
          <div>วันที่ ______ / ______ / ______</div>
        </div>
      </footer>
    </article>
  );
}

function TaxInvoiceEditor({
  receipt,
  settings,
  branchName,
  hasMultipleBranches,
  onClose,
  onIssued,
}: {
  receipt: ReceiptDetailDto;
  settings: SystemSettings;
  branchName: string;
  hasMultipleBranches: boolean;
  onClose: () => void;
  onIssued: () => void;
}) {
  const [form, setForm] = useState(() =>
    createInitialForm(settings, hasMultipleBranches, receipt),
  );
  const [errors, setErrors] = useState<TaxInvoiceErrors>({});
  const [isIssuing, setIsIssuing] = useState(false);
  const [issueError, setIssueError] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSearchError, setCustomerSearchError] = useState('');
  const isReprint = receipt.taxInvoiceCustomer != null;
  const printableForm = receipt.taxInvoiceCustomer
    ? {
        ...form,
        buyerName: receipt.taxInvoiceCustomer.name,
        buyerTaxId: receipt.taxInvoiceCustomer.taxId,
        buyerPhone: receipt.taxInvoiceCustomer.phone ?? '',
        buyerAddress: receipt.taxInvoiceCustomer.address,
        buyerBranchType: receipt.taxInvoiceCustomer.branchType,
        buyerBranchNo: receipt.taxInvoiceCustomer.branchNo ?? '',
      }
    : form;
  const userId = useSession((state) => state.user?.id ?? 'anonymous');
  const customerSearchQuery = useQuery({
    queryKey: [
      'tax-invoice-customers',
      userId,
      receipt.branchId,
      customerSearch,
    ],
    queryFn: () =>
      dashboardApi.searchTaxInvoiceCustomers({
        query: customerSearch,
        branchId: receipt.branchId,
        limit: 10,
      }),
    enabled: false,
    retry: false,
  });

  const set = <K extends keyof TaxInvoiceFormState>(
    key: K,
    value: TaxInvoiceFormState[K],
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const searchCustomers = () => {
    if (digitsOnly(customerSearch).length < 9) {
      setCustomerSearchError('กรอกเลขผู้เสียภาษีหรือเบอร์โทรอย่างน้อย 9 หลัก');
      return;
    }
    setCustomerSearchError('');
    void customerSearchQuery.refetch();
  };

  const applyCustomer = (customer: TaxInvoiceCustomerDto) => {
    setForm((current) => ({
      ...current,
      buyerName: customer.name,
      buyerTaxId: customer.taxId,
      buyerPhone: customer.phone ?? '',
      buyerAddress: customer.address,
      buyerBranchType: customer.branchType,
      buyerBranchNo: customer.branchNo ?? '',
    }));
    setErrors((current) => ({
      ...current,
      buyerName: undefined,
      buyerTaxId: undefined,
      buyerPhone: undefined,
      buyerAddress: undefined,
      buyerBranchNo: undefined,
    }));
    setCustomerSearch(customer.taxId);
    setCustomerSearchError('');
  };

  const onPrint = async (event: FormEvent) => {
    event.preventDefault();
    const nextErrors = validate(printableForm);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsIssuing(true);
    setIssueError('');
    try {
      await dashboardApi.issueTaxInvoice(receipt.receiptNo, receipt.branchId, {
        name: printableForm.buyerName.trim(),
        taxId: printableForm.buyerTaxId.trim(),
        ...(printableForm.buyerPhone.trim()
          ? { phone: printableForm.buyerPhone.trim() }
          : {}),
        address: printableForm.buyerAddress.trim(),
        branchType: printableForm.buyerBranchType,
        ...(printableForm.buyerBranchType === 'branch'
          ? { branchNo: digitsOnly(printableForm.buyerBranchNo) }
          : {}),
      });
      onIssued();
      document.body.classList.add('tax-invoice-printing');
      try {
        window.print();
      } finally {
        document.body.classList.remove('tax-invoice-printing');
      }
    } catch {
      setIssueError(
        'บันทึกการออกใบกำกับภาษีไม่สำเร็จ กรุณาตรวจสอบการเชื่อมต่อแล้วลองอีกครั้ง',
      );
    } finally {
      setIsIssuing(false);
    }
  };

  return (
    <form className="-m-1" onSubmit={onPrint}>
      <div className="tax-invoice-screen-only grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-5 lg:max-h-[calc(82dvh-112px)] lg:overflow-y-auto lg:pr-2">
          <section className="space-y-4" aria-labelledby="seller-heading">
            <div>
              <h3 id="seller-heading" className="font-bold text-ink">
                ข้อมูลผู้ขาย
              </h3>
              <p className="mt-0.5 text-sm-label text-ink-3">
                ชื่อร้านและเลขผู้เสียภาษีดึงจากการตั้งค่าระบบ
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="ชื่อผู้ขาย" required error={errors.sellerName}>
                <Input
                  aria-label="ชื่อผู้ขาย"
                  value={form.sellerName}
                  onChange={(event) => set('sellerName', event.target.value)}
                />
              </Field>
              <Field
                label="เลขผู้เสียภาษีผู้ขาย"
                required
                error={errors.sellerTaxId}
              >
                <TaxIdInput
                  aria-label="เลขผู้เสียภาษีผู้ขาย"
                  value={form.sellerTaxId}
                  onChange={(value) => set('sellerTaxId', value)}
                />
              </Field>
              <Field
                className="sm:col-span-2"
                label="ที่อยู่ผู้ขาย"
                required
                error={errors.sellerAddress}
              >
                <textarea
                  aria-label="ที่อยู่ผู้ขาย"
                  className={TEXTAREA_CLASS}
                  value={form.sellerAddress}
                  onChange={(event) => set('sellerAddress', event.target.value)}
                  placeholder="บ้านเลขที่ ถนน แขวง/ตำบล เขต/อำเภอ จังหวัด รหัสไปรษณีย์"
                />
              </Field>
            </div>
            <BranchFields
              prefix="ผู้ขาย"
              type={form.sellerBranchType}
              branchNo={form.sellerBranchNo}
              error={errors.sellerBranchNo}
              onTypeChange={(value) => set('sellerBranchType', value)}
              onBranchNoChange={(value) => set('sellerBranchNo', value)}
            />
          </section>

          <section
            className="space-y-4 border-t border-line pt-5"
            aria-labelledby="buyer-heading"
          >
            <div>
              <h3 id="buyer-heading" className="font-bold text-ink">
                ข้อมูลผู้ซื้อ
              </h3>
              <p className="mt-0.5 text-sm-label text-ink-3">
                ค้นหาลูกค้าเดิม หรือกรอกข้อมูลใหม่เพื่อบันทึกไว้ใช้ครั้งถัดไป
              </p>
            </div>
            {isReprint ? (
              <div className="rounded-card border border-line bg-bg-warm/60 p-3 text-sm-label text-ink-2">
                ใช้ข้อมูลผู้ซื้อจากใบกำกับภาษีฉบับแรก
                เพื่อให้เอกสารที่พิมพ์ซ้ำตรงกับต้นฉบับ
              </div>
            ) : (
              <div className="rounded-card border border-line bg-bg-warm/60 p-3">
                <div className="mb-2 font-semibold text-ink">
                  ค้นหาลูกค้าเดิม
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    aria-label="ค้นหาลูกค้าใบกำกับภาษี"
                    inputMode="numeric"
                    value={customerSearch}
                    onChange={(event) => {
                      setCustomerSearch(event.target.value);
                      setCustomerSearchError('');
                    }}
                    placeholder="เลขผู้เสียภาษีหรือเบอร์โทร"
                  />
                  <Btn
                    type="button"
                    onClick={searchCustomers}
                    disabled={customerSearchQuery.isFetching}
                  >
                    {customerSearchQuery.isFetching
                      ? 'กำลังค้นหา...'
                      : 'ค้นหาลูกค้า'}
                  </Btn>
                </div>
                {customerSearchError && (
                  <div role="alert" className="mt-2 text-sm-label text-danger">
                    {customerSearchError}
                  </div>
                )}
                {customerSearchQuery.isError && (
                  <div role="alert" className="mt-2 text-sm-label text-danger">
                    ค้นหาลูกค้าไม่สำเร็จ กรุณาลองอีกครั้ง
                  </div>
                )}
                {customerSearchQuery.data &&
                  customerSearchQuery.data.customers.length === 0 && (
                    <div className="mt-2 text-sm-label text-ink-3">
                      ไม่พบลูกค้าที่ตรงกับข้อมูลค้นหา
                    </div>
                  )}
                {customerSearchQuery.data &&
                  customerSearchQuery.data.customers.length > 0 && (
                    <div
                      aria-label="ผลการค้นหาลูกค้า"
                      className="mt-3 space-y-2"
                    >
                      {customerSearchQuery.data.customers.map((customer) => (
                        <div
                          key={`${customer.branchId}:${digitsOnly(customer.taxId)}`}
                          className="flex flex-col gap-2 rounded-btn border border-line bg-surface p-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div>
                            <div className="font-semibold text-ink">
                              {customer.name}
                            </div>
                            <div className="text-sm-label text-ink-3">
                              เลขผู้เสียภาษี {customer.taxId}
                              {customer.phone ? ` · โทร ${customer.phone}` : ''}
                            </div>
                          </div>
                          <Btn
                            type="button"
                            size="sm"
                            onClick={() => applyCustomer(customer)}
                          >
                            ใช้ข้อมูล
                          </Btn>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="ชื่อบุคคล / บริษัท"
                required
                error={errors.buyerName}
              >
                <Input
                  aria-label="ชื่อผู้ซื้อ"
                  value={form.buyerName}
                  disabled={isReprint}
                  onChange={(event) => set('buyerName', event.target.value)}
                />
              </Field>
              <Field
                label="เลขผู้เสียภาษีผู้ซื้อ"
                required
                error={errors.buyerTaxId}
              >
                <TaxIdInput
                  aria-label="เลขผู้เสียภาษีผู้ซื้อ"
                  value={form.buyerTaxId}
                  disabled={isReprint}
                  onChange={(value) => set('buyerTaxId', value)}
                />
              </Field>
              <Field
                label="เบอร์โทร"
                hint="ไม่บังคับ"
                error={errors.buyerPhone}
              >
                <Input
                  aria-label="เบอร์โทรผู้ซื้อ"
                  inputMode="tel"
                  value={form.buyerPhone}
                  disabled={isReprint}
                  onChange={(event) => set('buyerPhone', event.target.value)}
                  placeholder="08x-xxx-xxxx"
                />
              </Field>
              <Field
                className="sm:col-span-2"
                label="ที่อยู่ผู้ซื้อ"
                required
                error={errors.buyerAddress}
              >
                <textarea
                  aria-label="ที่อยู่ผู้ซื้อ"
                  className={TEXTAREA_CLASS}
                  value={form.buyerAddress}
                  disabled={isReprint}
                  onChange={(event) => set('buyerAddress', event.target.value)}
                  placeholder="บ้านเลขที่ ถนน แขวง/ตำบล เขต/อำเภอ จังหวัด รหัสไปรษณีย์"
                />
              </Field>
            </div>
            <BranchFields
              prefix="ผู้ซื้อ"
              type={form.buyerBranchType}
              branchNo={form.buyerBranchNo}
              error={errors.buyerBranchNo}
              disabled={isReprint}
              onTypeChange={(value) => set('buyerBranchType', value)}
              onBranchNoChange={(value) => set('buyerBranchNo', value)}
            />
            <Field label="หมายเหตุ" hint="ไม่บังคับ">
              <textarea
                aria-label="หมายเหตุ"
                className={TEXTAREA_CLASS}
                value={form.note}
                onChange={(event) => set('note', event.target.value)}
              />
            </Field>
          </section>

          <div className="sticky bottom-0 flex justify-end gap-2 border-t border-line bg-surface/95 py-3 backdrop-blur-sm">
            {issueError && (
              <div role="alert" className="mr-auto text-body text-danger">
                {issueError}
              </div>
            )}
            <Btn type="button" onClick={onClose}>
              ยกเลิก
            </Btn>
            <Btn kind="primary" type="submit" disabled={isIssuing}>
              {isIssuing ? 'กำลังบันทึก...' : 'พิมพ์ใบกำกับภาษี'}
            </Btn>
          </div>
        </div>

        <aside
          aria-label="ตัวอย่างเอกสาร"
          className="min-w-0 rounded-card border border-line bg-bg-warm/70 p-3 lg:sticky lg:top-0"
        >
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="text-body font-bold text-ink">ตัวอย่างเอกสาร</h3>
              <p className="text-sm-label text-ink-3">กระดาษ A4</p>
            </div>
            <span className="font-mono text-sm-label text-ink-3">
              {fmtDate(new Date(receipt.soldAt))}
            </span>
          </div>
          <div className="tax-invoice-preview-frame flex justify-center overflow-hidden rounded-btn bg-slate-200/70 p-2">
            <InvoiceDocument
              receipt={receipt}
              branchName={branchName}
              form={printableForm}
            />
          </div>
        </aside>
      </div>
    </form>
  );
}

export function TaxInvoiceModal({
  open,
  receipt,
  onClose,
  onIssued,
}: {
  open: boolean;
  receipt: ReceiptDetailDto;
  onClose: () => void;
  onIssued: () => void;
}) {
  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
    enabled: open,
  });
  const branchesQuery = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
    enabled: open,
  });
  const branches = branchesQuery.data ?? [];
  const branchName =
    branches.find((branch) => branch.id === receipt.branchId)?.name ??
    receipt.branchId;

  return (
    <Modal
      open={open}
      title={
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span>ออกใบกำกับภาษี</span>
          <span className="font-mono text-sm-label font-semibold text-ink-3">
            #{receipt.receiptNo}
          </span>
        </span>
      }
      onClose={onClose}
      className="tax-invoice-dialog !w-[min(980px,94vw)] !max-h-[82dvh]"
    >
      {settingsQuery.isError ? (
        <div className="mx-auto flex max-w-sm flex-col items-center py-12 text-center">
          <h3 className="font-bold text-ink">โหลดข้อมูลร้านไม่สำเร็จ</h3>
          <p className="mt-1 text-body text-ink-3">
            กรุณาตรวจสอบการเชื่อมต่อแล้วลองอีกครั้ง
          </p>
          <Btn
            className="mt-4"
            type="button"
            onClick={() => void settingsQuery.refetch()}
          >
            ลองอีกครั้ง
          </Btn>
        </div>
      ) : settingsQuery.isLoading || !settingsQuery.data ? (
        <div
          aria-label="กำลังโหลดข้อมูลร้าน"
          className="grid gap-5 lg:grid-cols-[1fr_360px]"
        >
          <div className="space-y-4">
            <div className="h-5 w-28 rounded bg-line-2" />
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="h-16 rounded-btn bg-bg-warm" />
              <div className="h-16 rounded-btn bg-bg-warm" />
              <div className="h-24 rounded-btn bg-bg-warm sm:col-span-2" />
            </div>
          </div>
          <div className="hidden h-[440px] rounded-card bg-bg-warm lg:block" />
        </div>
      ) : (
        <TaxInvoiceEditor
          key={receipt.receiptNo}
          receipt={receipt}
          settings={settingsQuery.data}
          branchName={branchName}
          hasMultipleBranches={branches.length > 1}
          onClose={onClose}
          onIssued={onIssued}
        />
      )}
    </Modal>
  );
}
